import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import * as customerAPI from '../api/customer';
import * as itrAPI from '../api/itr';
import * as superadminAPI from '../api/superadmin';
import * as subadminAPI from '../api/subadmin';
import API from '../api/axios';
import { IoMdClose, IoIosArrowBack } from "react-icons/io";

const GlobalSearchOverlay = () => {
    const { user, globalSearchQuery, setGlobalSearchQuery, showGlobalSearch, setShowGlobalSearch, isCollapsed } = useContext(AuthContext);
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [itrs, setItrs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedCards, setExpandedCards] = useState(new Set());

    const handleViewDetails = (type, data) => {
        setGlobalSearchQuery('');
        setShowGlobalSearch(false);
        if (user?.issuperadmin) {
            if (type === 'itr') {
                navigate('/superadmin/ITR', { state: { openITRModal: true, itrData: data } });
            } else if (type === 'customer') {
                navigate('/superadmin/customers', { state: { openCustomerModal: true, customerData: data } });
            }
        } else if (user?.issubadmin) {
            // Subadmin handles both through the same view/modal on the customers page
            navigate('/subadmin/customers', { state: { openViewModal: true, customerData: data } });
        }
        // Add logic for other roles if needed later
    };

    const toggleExpand = (id) => {
        setExpandedCards(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    useEffect(() => {
        if (showGlobalSearch) {
            fetchData();
        }
    }, [showGlobalSearch]);

    const fetchData = async () => {
        setLoading(true);
        try {
            let custData = [];
            let itrData = [];

            if (user?.issuperadmin) {
                [custData, itrData] = await Promise.all([
                    superadminAPI.getCustomers(),
                    superadminAPI.getITRs()
                ]);
            } else if (user?.issubadmin) {
                const response = await subadminAPI.getCustomersITR();
                const rawItrs = response.data || [];
                // Normalize: group by PAN number or customer_id to avoid collapsing records
                const customerMap = new Map();
                itrData = rawItrs.map(item => {
                    const custId = item.customer_id;
                    const pan = item.pan_number || item.customer_pan_number || item.Pan_Number;
                    const name = item.name || item.customer_name || item.Customer_Name;

                    // Use a grouping key that combines customer_id and pan to be safe
                    const groupKey = custId ? `cust_${custId}` : (pan ? `pan_${pan}` : `itr_${item.itr_id || item.id}`);

                    if (!customerMap.has(groupKey)) {
                        customerMap.set(groupKey, {
                            id: custId || groupKey,
                            name: name,
                            pan_number: pan,
                            mobile_no: item.mobile_no || item.customer_mobile || item.Mobile_No,
                            mail_id: item.mail_id || item.customer_email || item.Email,
                            father_name: item.father_name || item.customer_father_name || item.Father_Name,
                            dob: item.dob || item.customer_dob || item.Date_Of_Birth,
                            adhar_number: item.adhar_number || item.customer_adhar_number || item.Adhar_Number,
                            address: item.address || item.customer_address || item.Address,
                            category: item.category || item.customer_category || item.Category,
                            account_number: item.account_number || item.customer_account_number || item.Account_Number,
                            ifsc_code: item.ifsc_code || item.customer_ifsc_code || item.Ifsc_Code
                        });
                    }
                    return {
                        ...item,
                        id: item.itr_id || item.id,
                        customer_id: custId || groupKey,
                        asst_year: item.asst_year || item.itr_asst_year,
                        status: item.status || item.itr_status
                    };
                });
                custData = Array.from(customerMap.values());
            } else if (user?.isca) {
                const caId = user?.id;
                const response = await API.get(`/ca/assigned-itrs/${caId}`);
                const rawItrs = response.data || [];
                const customerMap = new Map();
                itrData = rawItrs.map(item => {
                    const custId = item.customer_id;
                    const pan = item.name || item.customer_name || item.Customer_Name; // Wait, PAN field name
                    const panActual = item.pan_number || item.customer_pan_number || item.Pan_Number;

                    const groupKey = custId ? `cust_${custId}` : (panActual ? `pan_${panActual}` : `itr_${item.itr_id || item.id}`);

                    if (!customerMap.has(groupKey)) {
                        customerMap.set(groupKey, {
                            id: custId || groupKey,
                            name: item.name || item.customer_name || item.Customer_Name,
                            pan_number: panActual,
                            mobile_no: item.mobile_no || item.customer_mobile || item.Mobile_No,
                            mail_id: item.mail_id || item.customer_email || item.Email,
                            father_name: item.father_name || item.customer_father_name || item.Father_Name,
                            dob: item.dob || item.customer_dob || item.Date_Of_Birth,
                            adhar_number: item.adhar_number || item.customer_adhar_number || item.Adhar_Number,
                            address: item.address || item.customer_address || item.Address,
                            category: item.category || item.customer_category || item.Category,
                            account_number: item.account_number || item.customer_account_number || item.Account_Number,
                            ifsc_code: item.ifsc_code || item.customer_ifsc_code || item.Ifsc_Code
                        });
                    }
                    return {
                        ...item,
                        id: item.itr_id || item.id,
                        customer_id: custId || groupKey,
                        asst_year: item.itr_asst_year,
                        status: item.itr_status
                    };
                });
                custData = Array.from(customerMap.values());
            } else {
                [custData, itrData] = await Promise.all([
                    customerAPI.getCustomers(),
                    itrAPI.getITRs()
                ]);
            }

            setCustomers(Array.isArray(custData) ? custData : []);
            setItrs(Array.isArray(itrData) ? itrData : []);
        } catch (error) {
            console.error("Error fetching search data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setGlobalSearchQuery('');
        setShowGlobalSearch(false);
    };

    const filteredResults = useMemo(() => {
        if (!globalSearchQuery.trim()) return [];
        const query = globalSearchQuery.trim().toLowerCase();

        // For each customer, check if they or any of their ITRs match the query
        return customers.map(customer => {
            const name = (customer.name || '').toString().toLowerCase();
            const pan = (customer.pan_number || '').toString().toLowerCase();
            const mobile = (customer.mobile_no || '').toString().toLowerCase();

            const customerMatches = name.includes(query) || pan.includes(query) || mobile.includes(query);

            const matchingItrs = itrs.filter(itr => {
                if (itr.customer_id !== customer.id) return false;

                const asstYear = (itr.asst_year || '').toString().toLowerCase();
                const status = (itr.status || '').toString().toLowerCase();
                const itrPan = (itr.pan_number || '').toString().toLowerCase();
                const ack = (itr.ack_no || '').toString().toLowerCase();

                return asstYear.includes(query) ||
                    status.includes(query) ||
                    itrPan.includes(query) ||
                    ack.includes(query);
            });

            // If either the customer matches OR any of their ITRs match, include this result
            if (customerMatches || matchingItrs.length > 0) {
                return {
                    ...customer,
                    // If the customer matched, show all their ITRs. 
                    // If only some ITRs matched, we still show all associated ITRs for context? 
                    // Actually, let's show all associate ITRs if the customer matched, 
                    // but if ONLY ITRs matched, we could just show the matching ones.
                    // For now, let's keep all ITRs for the matched customer for a full view.
                    itrs: itrs.filter(itr => itr.customer_id === customer.id)
                };
            }
            return null;
        }).filter(Boolean);
    }, [globalSearchQuery, customers, itrs]);

    if (!showGlobalSearch) return null;

    return (
        <div className={`fixed inset-0 pt-16 bg-purple-100 ${isCollapsed ? 'md:ml-20' : 'md:ml-64'} z-40 overflow-y-auto animate-in fade-in duration-300`}>
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8 border-b pb-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBack}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center gap-2 text-purple-700 font-bold"
                        >
                            <IoIosArrowBack size={24} />
                            <span>Back</span>
                        </button>
                        <h2 className="text-2xl font-black text-gray-800">Search Results</h2>
                    </div>
                    <div className="text-sm font-bold text-gray-400 uppercase tracking-widest">
                        Query: <span className="text-purple-600">"{globalSearchQuery}"</span>
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                        <p className="text-gray-500 font-bold">Searching records...</p>
                    </div>
                ) : filteredResults.length > 0 ? (
                    <div className="grid gap-6">
                        {filteredResults.map(customer => (
                            <div key={customer.id} className="bg-white border rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden border-gray-100">
                                <div className="p-6 flex flex-col md:flex-row justify-between gap-6">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center text-purple-700 font-black">
                                                {customer.name?.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-lg font-black text-gray-800">{customer.name}</h3>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">#{customer.id}</p>
                                            </div>
                                            {(user?.issuperadmin || user?.issubadmin) && (
                                                <button
                                                    onClick={() => handleViewDetails('customer', customer)}
                                                    className="px-4 py-1.5 bg-purple-600 text-white rounded-xl text-[10px] font-bold uppercase transition-all hover:bg-purple-700 active:scale-95 shadow-sm"
                                                >
                                                    View Profile
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 text-sm">
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">PAN Number</p>
                                                <p className="font-bold text-gray-700">{customer.pan_number}</p>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Mobile Number</p>
                                                <p className="font-bold text-gray-700">{customer.mobile_no}</p>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-0.5">Email</p>
                                                <p className="font-bold text-gray-700">{customer.mail_id || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {customer.itrs && customer.itrs.length > 0 && (
                                        <div className="md:w-72 bg-gray-50 rounded-xl p-4">
                                            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                                <i className="fas fa-file-invoice"></i> ITR Information
                                            </h4>
                                            <div className="space-y-3">
                                                {customer.itrs.map(itr => (
                                                    <div key={itr.id} className="bg-white p-2 rounded-lg shadow-sm border border-gray-100">
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-xs font-black text-gray-700">AY {itr.asst_year}</span>
                                                            <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase ${(itr.status === 'completed' || itr.status === 'E-verification') ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                                {itr.status || 'Pending'}
                                                            </span>
                                                        </div>
                                                        <p className="text-[9px] text-gray-400 mt-1">Date: {new Date(itr.created_at).toLocaleDateString('en-GB')}</p>
                                                        {(user?.issuperadmin || user?.issubadmin) && (
                                                            <button
                                                                onClick={() => handleViewDetails('itr', itr)}
                                                                className="mt-2 w-full py-1 bg-white border border-purple-200 text-purple-600 rounded-lg text-[9px] font-bold uppercase hover:bg-purple-600 hover:text-white transition-all active:scale-95"
                                                            >
                                                                View Details
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Expandable Details Section */}
                                <div className="border-t border-gray-50 bg-gray-50/30 px-6 py-3">
                                    <button
                                        onClick={() => toggleExpand(customer.id)}
                                        className="text-[11px] font-black text-purple-700 uppercase tracking-widest flex items-center gap-2 hover:text-purple-900 transition-colors"
                                    >
                                        {expandedCards.has(customer.id) ? (
                                            <>
                                                <i className="fas fa-chevron-up"></i> Hide Details
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-chevron-down"></i> View More Details
                                            </>
                                        )}
                                    </button>

                                    {expandedCards.has(customer.id) && (
                                        <div className={`mt-4 grid grid-cols-1 ${customer.itrs && customer.itrs.length > 0 ? 'md:grid-cols-2' : 'max-w-3xl'} gap-8 pb-4 animate-in slide-in-from-top-2 duration-300`}>
                                            {/* Extra Customer Info */}
                                            <div className="space-y-4">
                                                <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b pb-1">Additional Profile Info</h5>
                                                <div className="grid grid-cols-2 gap-4 text-[13px]">
                                                    <div>
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase">Father's Name</p>
                                                        <p className="font-bold text-gray-700">{customer.father_name || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase">DOB</p>
                                                        <p className="font-bold text-gray-700">
                                                            {customer.dob ? new Date(customer.dob).toLocaleDateString('en-GB') : 'N/A'}
                                                        </p>
                                                    </div>
                                                    <div className="col-span-2">
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase">Address</p>
                                                        <p className="font-bold text-gray-700 leading-tight">{customer.address || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase">Aadhaar Number</p>
                                                        <p className="font-bold text-gray-700">{customer.adhar_number || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase">Category</p>
                                                        <p className="font-bold text-gray-700">{customer.category || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase">Bank Account</p>
                                                        <p className="font-bold text-gray-700">{customer.account_number || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase">IFSC Code</p>
                                                        <p className="font-bold text-gray-700">{customer.ifsc_code || 'N/A'}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Extra ITR Info */}
                                            {customer.itrs && customer.itrs.length > 0 && (
                                                <div className="space-y-4">
                                                    <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b pb-1">Extended ITR Records</h5>
                                                    <div className="space-y-4">
                                                        {customer.itrs.map(itr => (
                                                            <div key={itr.id} className="bg-white p-3 rounded-xl border border-purple-100 shadow-sm">
                                                                <div className="flex justify-between items-start mb-2">
                                                                    <p className="text-[11px] font-black text-purple-700 lowercase bg-purple-50 px-2 py-0.5 rounded-md">ay {itr.asst_year}</p>
                                                                    <p className="text-[9px] font-bold text-gray-400 uppercase">Ack: {itr.ack_no || 'Pending'}</p>
                                                                </div>
                                                                <div className="grid grid-cols-2 gap-2 text-[11px]">
                                                                    <div>
                                                                        <p className="text-[8px] font-bold text-gray-400 uppercase">ITR Password</p>
                                                                        <p className="font-bold text-gray-600 line-clamp-1">{itr.itr_password || 'N/A'}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-[8px] font-bold text-gray-400 uppercase">Remark</p>
                                                                        <p className="font-bold text-gray-600 line-clamp-1">{itr.remark || 'N/A'}</p>
                                                                    </div>
                                                                    <div className="col-span-2">
                                                                        <p className="text-[8px] font-bold text-gray-400 uppercase">Description</p>
                                                                        <p className="text-gray-600 leading-tight italic">{itr.description || 'No additional description provided.'}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                            <i className="fas fa-search text-gray-200 text-3xl"></i>
                        </div>
                        <h3 className="text-xl font-black text-gray-800">No Records Found</h3>
                        <p className="text-gray-500 max-w-sm mt-2 font-medium">We couldn't find any customers matching your search query. Please try searching by full PAN, Name, or Mobile Number.</p>
                        <button
                            onClick={handleBack}
                            className="mt-6 px-6 py-2 bg-purple-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                        >
                            Return to Dashboard
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GlobalSearchOverlay;
